import React from 'react';
import {
  CondensedLarge,
  Dropdown,
  Form,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Modal,
  ModalSection,
  ParagraphHeader,
  useApi,
} from '@axiom/ui';
import type { Account, AfcSubmission } from '@axiom/validation';
import {
  legalTeamSizeValues,
  licensedLawyerValues,
  StateCodesConst,
} from '@axiom/const';

import type { InterviewFlowCandidate } from '../../types/interview-candidate.types';
import { AccountIntakeApi } from '../../api/account-intake-api';
import { TalentInterviewModalHeader } from '../TalentInterviewModalHeader/TalentInterviewModalHeader';
import { TalentInterviewModalFooter } from '../TalentInterviewModalFooter/TalentInterviewModalFooter';
import type { TalentInterviewLegalTeamQuestionsType } from '../../schema/talent-interview-legal-team-questions-modal-schema';
import { TalentInterviewLegalTeamQuestionsModalSchema } from '../../schema/talent-interview-legal-team-questions-modal-schema';

const stateCodes = StateCodesConst.StateCodesWithoutTerritories;

export interface LegalTeamQuestionsModalProps {
  accountId: Account['id'];
  candidate: InterviewFlowCandidate;
  onClose: () => void;
  onContinue: (data: Partial<TalentInterviewLegalTeamQuestionsType>) => void;
  submissionId?: AfcSubmission['id'];
}

export const TalentInterviewLegalTeamQuestionsModal = ({
  accountId,
  candidate,
  onClose,
  onContinue,
  submissionId,
}: LegalTeamQuestionsModalProps) => {
  const [{ data: intakeData }] = useApi(
    AccountIntakeApi.readAccountIntake(
      accountId,
      submissionId ? { submissionId } : undefined
    )
  );

  return (
    <Form
      initialValues={{
        legalTeamSize: intakeData?.legalTeamSize,
        licensedLawyer: intakeData?.licensedLawyer,
        licensedLawyerState: intakeData?.licensedLawyerState,
      }}
      name="INTERVIEW_LEGAL_TEAM_QUESTIONS_FORM"
      schema={TalentInterviewLegalTeamQuestionsModalSchema}
      onSubmit={data => {
        onContinue(data);
      }}
    >
      {({ fireSubmit }) => {
        return (
          <Modal name="LEGAL_TEAM_QUESTIONS_MODAL">
            <TalentInterviewModalHeader
              candidate={candidate}
              name="LEGAL_TEAM_QUESTIONS_MODAL_HEADER"
              onClose={onClose}
            />
            <ModalSection disableScrolling>
              <ParagraphHeader>Tell us about your legal team</ParagraphHeader>
              <Gutter top="8px" bottom="16px">
                <CondensedLarge>
                  We need to confirm a lawyer is available for our talent to
                  report to.
                </CondensedLarge>
              </Gutter>
              <Grid>
                <GridRow>
                  <GridColumn
                    largeScreenWidth={4}
                    tabletWidth={4}
                    mobileWidth={12}
                  >
                    <Dropdown
                      name="legalTeamSize"
                      label="Lawyers on your team"
                      options={Object.values(legalTeamSizeValues).map(
                        value => ({
                          label: value,
                          value,
                        })
                      )}
                      displayKey="label"
                      valueKey="value"
                      disableArrowKeyChange
                    />
                  </GridColumn>
                  <GridColumn
                    largeScreenWidth={8}
                    tabletWidth={8}
                    mobileWidth={12}
                  >
                    <Gutter top="8px" only="mobile" />
                    <Dropdown
                      name="licensedLawyer"
                      label="Lawyer who will be the Axiom talent’s primary contact"
                      options={Object.entries(licensedLawyerValues).map(
                        ([key, value]) => ({ label: value, value: key })
                      )}
                      displayKey="label"
                      valueKey="value"
                      disableArrowKeyChange
                    />
                  </GridColumn>
                </GridRow>
                <GridRow gutterTop="8px">
                  <GridColumn
                    largeScreenWidth={4}
                    tabletWidth={4}
                    mobileWidth={12}
                  >
                    <Dropdown
                      name="licensedLawyerState"
                      label="Primary contact location"
                      options={Object.entries(stateCodes).map(
                        ([key, value]) => ({ label: value, value: key })
                      )}
                      displayKey="label"
                      valueKey="value"
                      disableArrowKeyChange
                    />
                  </GridColumn>
                </GridRow>
              </Grid>
            </ModalSection>
            <TalentInterviewModalFooter
              name="LEGAL_TEAM_QUESTIONS_MODAL_FOOTER"
              onClose={onClose}
              onContinue={fireSubmit}
            />
          </Modal>
        );
      }}
    </Form>
  );
};
